import { Link, NavLink, useLocation } from "react-router-dom";
import { House, Users, ClipboardText, Receipt, Package, GearSix, List, X, SignOut, UsersThree } from "@phosphor-icons/react";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";

const BASE_NAV = [
  { to: "/", label: "Dashboard", icon: House, testid: "nav-dashboard" },
  { to: "/customers", label: "Customers", icon: Users, testid: "nav-customers" },
  { to: "/invoices", label: "Invoices", icon: Receipt, testid: "nav-invoices" },
  { to: "/work-orders", label: "Work Orders", icon: ClipboardText, testid: "nav-work-orders" },
  { to: "/catalog", label: "Catalog", icon: Package, testid: "nav-catalog" },
];

const OWNER_NAV = [
  { to: "/users", label: "Team", icon: UsersThree, testid: "nav-users" },
  { to: "/settings", label: "Settings", icon: GearSix, testid: "nav-settings" },
];

export default function Layout({ children }) {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const { user, isOwner, logout } = useAuth();

  const nav = [...BASE_NAV, ...(isOwner ? OWNER_NAV : [])];

  const SidebarNav = ({ onNav }) => (
    <nav className="flex flex-col gap-1 px-3 py-3">
      {nav.map(({ to, label, icon: Icon, testid }) => (
        <NavLink
          key={to}
          to={to}
          end={to === "/"}
          data-testid={testid}
          onClick={onNav}
          className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
        >
          <Icon size={20} weight="regular" />
          <span>{label}</span>
        </NavLink>
      ))}
    </nav>
  );

  const UserFooter = () => (
    <div className="mt-auto p-4 border-t hairline bg-white">
      <div className="flex items-center justify-between">
        <div className="min-w-0">
          <div className="text-sm text-slate-900 font-medium truncate" data-testid="me-username">{user?.name || user?.username}</div>
          <div className="text-[11px] text-slate-500 font-mono uppercase tracking-wider">{user?.role}</div>
        </div>
        <button className="btn-ghost !py-1.5 !px-2" onClick={logout} data-testid="logout-btn" title="Sign out">
          <SignOut size={16} />
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex" data-testid="app-shell">
      {/* Desktop Sidebar */}
      <aside
        className="hidden md:flex md:w-64 flex-col border-r hairline sticky top-0 h-screen z-10"
        style={{ background: "var(--sidebar)" }}
      >
        <Link to="/" className="flex items-center gap-3 px-5 py-6" data-testid="brand-link">
          <div className="w-9 h-9 flex items-center justify-center rounded-lg" style={{ background: "var(--primary)" }}>
            <span className="font-display text-slate-900 text-lg leading-none">L</span>
          </div>
          <div>
            <div className="font-display text-lg leading-none text-slate-900">LaserForge</div>
            <div className="text-[10px] text-slate-500 font-mono uppercase tracking-widest mt-1">Shop Ops</div>
          </div>
        </Link>
        <div className="border-t hairline mx-3" />
        <SidebarNav />
        <UserFooter />
      </aside>

      {/* Mobile Header */}
      <div
        className="md:hidden fixed top-0 left-0 right-0 z-30 flex items-center justify-between px-4 py-3 border-b hairline"
        style={{ background: "var(--surface)" }}
      >
        <Link to="/" className="flex items-center gap-2" data-testid="brand-link-mobile">
          <div className="w-8 h-8 flex items-center justify-center rounded-md" style={{ background: "var(--primary)" }}>
            <span className="font-display text-slate-900 text-base leading-none">L</span>
          </div>
          <div className="font-display text-base text-slate-900 leading-none">LaserForge</div>
        </Link>
        <button data-testid="mobile-menu-toggle" onClick={() => setOpen(true)} className="btn-ghost !py-1.5 !px-2">
          <List size={22} />
        </button>
      </div>

      {/* Mobile Drawer */}
      {open && (
        <div className="fixed inset-0 z-40 md:hidden" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-black/30" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-0 h-full w-72 border-l hairline flex flex-col" style={{ background: "var(--sidebar)" }}>
            <div className="flex items-center justify-between p-4 border-b hairline">
              <span className="font-display text-base text-slate-900">Menu</span>
              <button onClick={() => setOpen(false)} data-testid="mobile-drawer-close" className="btn-ghost !py-1 !px-2">
                <X size={20} />
              </button>
            </div>
            <SidebarNav onNav={() => setOpen(false)} />
            <UserFooter />
          </div>
        </div>
      )}

      <main className="flex-1 min-w-0 pt-14 md:pt-0 relative" style={{ background: "var(--bg)" }}>
        <div className="mx-auto max-w-[1400px] px-4 md:px-8 py-6 md:py-10" key={loc.pathname}>
          {children}
        </div>
      </main>
    </div>
  );
}
